import React, { useState, useRef, useEffect } from 'react';
import '../../styles/productDetail.css';

/**
 * Responsive image gallery component.
 * Displays large display image on top and thumbnail gallery horizontally below.
 * Supports desktop hover-zoom tracking, mobile tap fullscreen overlay,
 * swipe gesture navigation, and synchronized indicators.
 *
 * @param {Object} props
 * @param {Array} props.images - Array of product image objects (with url) or strings
 * @param {string} props.sectionColor - Section theme color (e.g. hex code)
 */
const ProductGallery = ({ images = [], sectionColor }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [fadeUrl, setFadeUrl] = useState('');
  const [isFading, setIsFading] = useState(false);
  
  // Desktop Hover Zoom state
  const [zoomStyle, setZoomStyle] = useState({
    transform: 'scale(1)',
    transformOrigin: 'center center'
  });

  // Mobile Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  const viewportRef = useRef(null);
  const fullscreenViewportRef = useRef(null);

  // Normalize image inputs (handles objects { url } and legacy string arrays) with fallback
  const finalImageUrls = React.useMemo(() => {
    const urls = images.map((img) => 
      typeof img === 'string' ? img : img?.url
    ).filter(Boolean);
    return urls.length === 0 ? ['/placeholder_product.png'] : urls;
  }, [images]);

  // Set initial image URL
  useEffect(() => {
    setFadeUrl(finalImageUrls[selectedIndex] || '/placeholder_product.png');
  }, [finalImageUrls, selectedIndex]);

  // Desktop Hover Zoom mouse move coordinator
  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomStyle({
      transform: 'scale(2.2)',
      transformOrigin: `${x}% ${y}%`
    });
  };

  const handleMouseLeave = () => {
    setZoomStyle({
      transform: 'scale(1)',
      transformOrigin: 'center center'
    });
  };

  // Handle smooth image fade switching when selectedIndex changes (Desktop/Clicks)
  const changeImageWithFade = (index) => {
    if (index === selectedIndex) return;
    setIsFading(true);
    // After transition duration, swap URL and clear fade state
    setTimeout(() => {
      setSelectedIndex(index);
      setIsFading(false);
    }, 150);
  };

  // Handle mobile swipe scroll detection to update active index
  const handleScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const width = e.target.offsetWidth;
    if (width > 0) {
      const index = Math.round(scrollLeft / width);
      // Only set state if the index actually changes
      if (index !== selectedIndex && index >= 0 && index < finalImageUrls.length) {
        setSelectedIndex(index);
        setFadeUrl(finalImageUrls[index]);
      }
    }
  };

  // Handle mobile fullscreen swipe scroll index update
  const handleFullscreenScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const width = e.target.offsetWidth;
    if (width > 0) {
      const index = Math.round(scrollLeft / width);
      if (index !== selectedIndex && index >= 0 && index < finalImageUrls.length) {
        setSelectedIndex(index);
        setFadeUrl(finalImageUrls[index]);
      }
    }
  };

  // Scroll mobile viewport to selected index
  const scrollToImageMobile = (index) => {
    setSelectedIndex(index);
    setFadeUrl(finalImageUrls[index]);
    if (viewportRef.current) {
      const width = viewportRef.current.offsetWidth;
      viewportRef.current.scrollTo({
        left: index * width,
        behavior: 'smooth'
      });
    }
  };

  // Scroll mobile fullscreen viewport to selected index when opened
  useEffect(() => {
    if (isFullscreen && fullscreenViewportRef.current) {
      const timer = setTimeout(() => {
        if (fullscreenViewportRef.current) {
          const width = fullscreenViewportRef.current.offsetWidth;
          fullscreenViewportRef.current.scrollLeft = selectedIndex * width;
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isFullscreen, selectedIndex]);

  // Handle window resizing to keep the active image aligned in mobile viewport
  useEffect(() => {
    const handleResize = () => {
      if (viewportRef.current && window.innerWidth <= 768) {
        const width = viewportRef.current.offsetWidth;
        viewportRef.current.scrollLeft = selectedIndex * width;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedIndex]);

  // Section style variables
  const galleryStyle = {
    '--section-color': sectionColor,
    '--section-color-glow': sectionColor.startsWith('#') 
      ? `${sectionColor}26` 
      : 'rgba(255, 106, 0, 0.15)',
  };

  return (
    <div className="product-layout-gallery-group" style={galleryStyle}>
      {/* ===================================================
         DESKTOP VIEW (Side-by-Side Layout)
         =================================================== */}
      <div className={`gallery-desktop-wrapper ${finalImageUrls.length > 1 ? 'has-thumbnails' : ''}`}>
        {finalImageUrls.length > 1 && (
          <div className="thumbnail-column">
            {finalImageUrls.map((url, idx) => (
              <img
                key={`thumb-desktop-${idx}`}
                src={url}
                alt={`Thumbnail ${idx + 1}`}
                className={`thumbnail-item ${idx === selectedIndex ? 'active' : ''}`}
                onClick={() => changeImageWithFade(idx)}
                loading="lazy"
              />
            ))}
          </div>
        )}

        <div 
          className="gallery-display-container desktop-gallery-view"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <img
            src={fadeUrl}
            alt="Product Display View"
            className={`gallery-display-image ${isFading ? 'fading' : ''}`}
            style={zoomStyle}
            loading="eager"
          />
        </div>
      </div>

      {/* ===================================================
         MOBILE VIEW (Scroll Snap Swipeable Image)
         =================================================== */}
      <div 
        className="gallery-display-container mobile-gallery-view"
        onClick={() => setIsFullscreen(true)}
      >
        <div 
          className="gallery-swipeable-viewport"
          ref={viewportRef}
          onScroll={handleScroll}
        >
          <div className="gallery-swipe-track">
            {finalImageUrls.map((url, idx) => (
              <div className="gallery-slide" key={`mobile-slide-${idx}`}>
                <img
                  src={url}
                  alt={`Product View Mobile ${idx + 1}`}
                  loading={idx === 0 ? "eager" : "lazy"}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Swipe Indicators Dots */}
      {finalImageUrls.length > 1 && (
        <div className="gallery-dots-row">
          {finalImageUrls.map((_, idx) => (
            <div
              key={`dot-${idx}`}
              className={`gallery-dot ${idx === selectedIndex ? 'active' : ''}`}
              onClick={() => scrollToImageMobile(idx)}
            />
          ))}
        </div>
      )}

      {/* Mobile Horizontal Thumbnail Slider */}
      {finalImageUrls.length > 1 && (
        <div className="thumbnail-rail-horizontal">
          {finalImageUrls.map((url, idx) => (
            <img
              key={`thumb-mobile-${idx}`}
              src={url}
              alt={`Thumbnail ${idx + 1}`}
              className={`thumbnail-item ${idx === selectedIndex ? 'active' : ''}`}
              onClick={() => scrollToImageMobile(idx)}
              loading="lazy"
            />
          ))}
        </div>
      )}

      {/* Mobile Full Screen Viewer Overlay Portal Modal */}
      {isFullscreen && (
        <div className="fullscreen-gallery-overlay">
          <button 
            className="fullscreen-close-btn" 
            onClick={() => setIsFullscreen(false)}
            aria-label="Close Fullscreen View"
          >
            ✕
          </button>
          <div 
            className="fullscreen-viewport"
            ref={fullscreenViewportRef}
            onScroll={handleFullscreenScroll}
          >
            <div className="fullscreen-track" style={{ display: 'flex', width: '100%', height: '100%' }}>
              {finalImageUrls.map((url, idx) => (
                <div className="fullscreen-slide" key={`fs-slide-${idx}`}>
                  <img src={url} alt={`Full view ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
